import {
  ComponentPropsWithoutRef,
  type ComponentType,
  type ReactNode,
} from 'react';

import { Link, Text } from '@backstage/ui';

import { ResourceNameLink } from '../ResourceNameLink';

// Component map for rendering
export interface ComponentMap {
  bold?: ComponentType<{ children: ReactNode }>;
  italic?: ComponentType<{ children: ReactNode }>;
  link?: ComponentType<{ href: string; children: ReactNode }>;
  image?: ComponentType<{ src: string; alt: string }>;
  resource?: ComponentType<{ resourceRef: string; children: string }>;
  code?: ComponentType<{ children: ReactNode }>;
}

// Default components
export const DEFAULT_MARKDOWN_COMPONENTS: ComponentMap = {
  bold: ({ children }) => <strong>{children}</strong>,
  italic: ({ children }) => <em>{children}</em>,
  link: ({ href, children }) => <Link href={href}>{children}</Link>,
  image: ({ src, alt }) => <img src={src} alt={alt} />,
  resource: ({ resourceRef, children }) => (
    <strong>
      <ResourceNameLink name={resourceRef} text={children} />
    </strong>
  ),
  code: ({ children }) => <code>{children}</code>,
};

// Pattern matchers with priority order (more specific patterns first)
const patterns = [
  // Image: ![alt](url)
  {
    name: 'image' as const,
    regex: /!\[([^\]]*)\]\(([^)]+)\)/,
  },
  // Resource: @[text](ref)
  {
    name: 'resource' as const,
    regex: /@\[([^\]]*)\]\(([^)]+)\)/,
  },
  // Link: [text](url)
  {
    name: 'link' as const,
    regex: /\[([^\]]+)\]\(([^)]+)\)/,
  },
  // Bold: **text** or __text__
  {
    name: 'bold' as const,
    regex: /(\*\*|__)(.+?)\1/,
  },
  // Italic: *text* or _text_
  {
    name: 'italic' as const,
    regex: /(\*|_)(.+?)\1/,
  },
  // Code: `text`
  {
    name: 'code' as const,
    regex: /`([^`]+)`/,
  },
];

function parseRecursive(
  input: string,
  components: ComponentMap,
  keyPrefix = '0',
): ReactNode[] {
  if (!input) return [];

  const results: ReactNode[] = [];

  // Find the first matching pattern
  let earliestMatch: {
    name: string;
    index: number;
    length: number;
    groups: string[];
  } | null = null;

  for (const pattern of patterns) {
    const match = input.match(pattern.regex);
    if (match && match.index !== undefined) {
      if (!earliestMatch || match.index < earliestMatch.index) {
        earliestMatch = {
          name: pattern.name,
          index: match.index,
          length: match[0].length,
          groups: match.slice(1),
        };
      }
    }
  }

  // No patterns found, return plain text
  if (!earliestMatch) {
    return [input];
  }

  const { name, index, length, groups } = earliestMatch;

  // Add text before the match
  if (index > 0) {
    results.push(input.substring(0, index));
  }

  // Create the appropriate element
  const key = `${keyPrefix}-${name}-${index}`;

  switch (name) {
    case 'bold': {
      const BoldComponent =
        components.bold || DEFAULT_MARKDOWN_COMPONENTS.bold!;
      const content = groups[1]; // The text inside bold markers
      results.push(
        <BoldComponent key={key}>
          {parseRecursive(content, components, `${key}-content`)}
        </BoldComponent>,
      );
      break;
    }

    case 'italic': {
      const ItalicComponent =
        components.italic || DEFAULT_MARKDOWN_COMPONENTS.italic!;
      const content = groups[1]; // The text inside italic markers
      results.push(
        <ItalicComponent key={key}>
          {parseRecursive(content, components, `${key}-content`)}
        </ItalicComponent>,
      );
      break;
    }

    case 'code': {
      const CodeComponent =
        components.code || DEFAULT_MARKDOWN_COMPONENTS.code!;
      const content = groups[0]; // The text inside code markers
      results.push(<CodeComponent key={key}>{content}</CodeComponent>);
      break;
    }

    case 'link': {
      const LinkComponent =
        components.link || DEFAULT_MARKDOWN_COMPONENTS.link!;
      const [text, url] = groups;
      // Pass text as-is without recursive parsing
      results.push(
        <LinkComponent key={key} href={url}>
          {text}
        </LinkComponent>,
      );
      break;
    }

    case 'image': {
      const ImageComponent =
        components.image || DEFAULT_MARKDOWN_COMPONENTS.image!;
      const [alt, url] = groups;
      results.push(<ImageComponent key={key} src={url} alt={alt} />);
      break;
    }

    case 'resource': {
      const ResourceComponent =
        components.resource || DEFAULT_MARKDOWN_COMPONENTS.resource!;
      const [text, resourceRef] = groups;
      // Pass text as-is without recursive parsing
      results.push(
        <ResourceComponent key={key} resourceRef={resourceRef}>
          {text}
        </ResourceComponent>,
      );
      break;
    }
    default:
      // Unknown pattern name - should not happen with current regex patterns
      break;
  }

  // Recursively parse the rest of the string
  const remaining = input.substring(index + length);
  if (remaining) {
    results.push(
      ...parseRecursive(
        remaining,
        components,
        `${keyPrefix}-${index + length}`,
      ),
    );
  }

  return results;
}

// Helper to add spacing between elements
function addSpacing(nodes: ReactNode[]): ReactNode[] {
  const result: ReactNode[] = [];

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const nextNode = i < nodes.length - 1 ? nodes[i + 1] : null;

    result.push(node);

    if (nextNode === null) continue;

    const currentIsString = typeof node === 'string';
    const nextIsString = typeof nextNode === 'string';
    const currentIsElement =
      !currentIsString && node !== null && node !== undefined;
    const nextIsElement =
      !nextIsString && nextNode !== null && nextNode !== undefined;

    // Add space between elements if needed
    if (currentIsElement && nextIsElement) {
      // Two adjacent elements always need a space
      result.push(' ');
    } else if (currentIsElement && nextIsString) {
      // Element followed by string - add space if string doesn't start with space/punctuation
      if (!nextNode.match(/^[\s.,;:!?)}\]]/)) {
        result.push(' ');
      }
    } else if (currentIsString && nextIsElement) {
      // String followed by element - add space if string doesn't end with space
      if (!node.match(/\s$/)) {
        result.push(' ');
      }
    }
  }

  return result;
}

function renderMarkdown(
  input: string,
  components: ComponentMap = {},
): ReactNode {
  const mergedComponents = { ...DEFAULT_MARKDOWN_COMPONENTS, ...components };
  const result = parseRecursive(input, mergedComponents);

  // Add spacing between elements
  const spacedResult = addSpacing(result);

  // Return a fragment if multiple elements, single element otherwise
  if (spacedResult.length === 0) return null;
  if (spacedResult.length === 1) return spacedResult[0];
  return <>{spacedResult}</>;
}

export interface SimpleMarkdownProps
  extends Omit<ComponentPropsWithoutRef<typeof Text>, 'children'> {
  content: string;
  components?: ComponentMap;
}

export const SimpleMarkdown = ({
  content,
  components,
  ...props
}: SimpleMarkdownProps) => {
  return <Text {...props}>{renderMarkdown(content, components)}</Text>;
};
